import { ErrorCode } from '@shared/types'
import { useActor } from '@ui-common/hooks/useActor'
import { ERROR, INITIAL } from '@ui-common/state/states'
import { ErrorVisualMode } from '@ui-components/containers/ErrorComponentTypes'
import { LogoIconType } from '@ui-components/global/ComponentProps'
import { useSelector } from '@xstate/react'
import { JSX, PropsWithChildren, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { AnyActorRef } from 'xstate'

import {
  TransitionErrorIndicatorProps,
  TransitionErrorIndicatorWithButtonProps,
} from '../../components/TransitionErrorIndicator'
import { TransitionStep } from '../TransitionStep'
import { GET_DATA, getDataMachine, GetDataOutputCallback } from './getDataMachine'

export interface GetDataStepProps<TData> {
  parentService?: AnyActorRef
  callbacks?: GetDataOutputCallback<TData>
  getDataCallback: () => Promise<TData> | undefined
  onAbort?: () => unknown
  heading?: string
  text?: string
  getErrorProperties?: (errorCode?: ErrorCode, requestId?: string) => TransitionErrorIndicatorProps | undefined
  icon?: LogoIconType
  testId?: string
}

export const GetDataStep = <TData,>(props: PropsWithChildren<GetDataStepProps<TData>>): JSX.Element => {
  const intl = useIntl()

  const { testId } = props

  const machine = getDataMachine({ parentService: props.parentService, callbacks: props.callbacks })
  const actor = useActor(machine, { input: {} })
  const { state, error } = useSelector(actor, ({ value, context }) => ({
    state: value,
    ...context,
  }))

  const getErrorProperties: (errorCode?: ErrorCode) => TransitionErrorIndicatorProps | undefined = (errorCode) => {
    if (errorCode === ErrorCode.OtherNonTechnical) {
      return {
        mode: ErrorVisualMode.TechnicalError,
        heading: intl.formatMessage({ id: 'common-non-technical-error-heading' }),
        text: intl.formatMessage({ id: 'common-non-technical-error-text' }),
      }
    }
    return undefined
  }

  const { getDataCallback, heading, text, onAbort, children, icon } = props

  useEffect(() => {
    actor.send({ type: 'START', data: { callback: getDataCallback } })
  }, [getDataCallback, actor])

  const isErrored = state === ERROR
  const isLoading = state === GET_DATA || state === INITIAL
  const errorCode = error?.errorCode
  const requestId = error?.requestId

  let errorProperties: TransitionErrorIndicatorProps | undefined = getErrorProperties(errorCode)

  if (isErrored && props.getErrorProperties) {
    errorProperties = props.getErrorProperties(errorCode, requestId)
  }

  return (
    <TransitionStep
      errorPropertiesOverride={errorProperties}
      error={error}
      isLoading={isLoading}
      isErrored={isErrored}
      heading={heading}
      text={text}
      onRetry={
        errorProperties
          ? (errorProperties as TransitionErrorIndicatorWithButtonProps).action?.onClick
          : () => actor.send({ type: 'RETRY' })
      }
      icon={icon}
      onAbort={onAbort}
      testId={testId}
    >
      {children}
    </TransitionStep>
  )
}
